<template>
  <ion-page>
    <ion-tabs>
      <ion-router-outlet name="order-tabs"></ion-router-outlet>
      <AdminFooter
        :preparingOrders="preparingOrders"
        :readyOrders="readyOrders"
      />
    </ion-tabs>
  </ion-page>
</template>
<script>
import { IonPage, IonTabs, IonRouterOutlet, toastController } from "@ionic/vue";
import AdminFooter from "@/components/admin/AdminFooter.vue";
import { printOrderTicket } from "@/services/orderPrintService";

import axios from "axios";
import { defineComponent } from "vue";

const BASE_URI =
  process.env.VUE_APP_BASE_URI !== undefined
    ? process.env.VUE_APP_BASE_URI
    : "https://api.onecoffeeapp.com.au";

export default defineComponent({
  name: "OrderTabs",
  components: {
    IonPage,
    IonTabs,
    IonRouterOutlet,
    AdminFooter,
  },
  provide() {
    return {
      refreshOrders: this.triggerRefresh,
    };
  },
  data() {
    return {
      promptMessage: "",
      preparingOrders: 0,
      readyOrders: 0,
      intervalnotif: null,
      // Auto-print state
      printedOrderIds: [],
      pendingMarkOrders: [],
      isAutoPrinting: false,
    };
  },
  methods: {
    async showToast(message, color = "medium") {
      const toast = await toastController.create({
        message: message,
        duration: 3000,
        position: "bottom",
        color: color,
      });
      await toast.present();
    },
    loadPrintedOrdersFromStorage() {
      try {
        const stored = localStorage.getItem("printedOrderIds");
        if (stored) {
          this.printedOrderIds = JSON.parse(stored);
          console.log(
            "[AutoPrint] Loaded printedOrderIds from localStorage:",
            this.printedOrderIds.length,
            "items"
          );
        }
      } catch (error) {
        console.error("Failed to load printed orders from storage:", error);
        this.printedOrderIds = [];
      }
    },
    savePrintedOrdersToStorage() {
      try {
        localStorage.setItem(
          "printedOrderIds",
          JSON.stringify(this.printedOrderIds)
        );
      } catch (error) {
        console.error("Failed to save printed orders to storage:", error);
      }
    },
    loadPendingMarkOrders() {
      try {
        const stored = localStorage.getItem("pendingMarkOrders");
        if (stored) {
          this.pendingMarkOrders = JSON.parse(stored);
        }
      } catch (error) {
        console.error("Failed to load pending mark orders:", error);
        this.pendingMarkOrders = [];
      }
    },
    savePendingMarkOrders() {
      try {
        localStorage.setItem(
          "pendingMarkOrders",
          JSON.stringify(this.pendingMarkOrders)
        );
      } catch (error) {
        console.error("Failed to save pending mark orders:", error);
      }
    },
    async retryPendingMarkOrders() {
      if (this.pendingMarkOrders.length === 0) return;

      const stillPending = [];
      for (const orderUuid of this.pendingMarkOrders) {
        const marked = await this.markOrderAsPrinted(orderUuid);
        if (!marked) {
          stillPending.push(orderUuid);
        }
      }

      this.pendingMarkOrders = stillPending;
      this.savePendingMarkOrders();
    },
    async getUnprintedOrders(cafeId) {
      let axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      // Fetch received orders that haven't been printed
      let url =
        BASE_URI +
        "/orders/admin/get-orders/?cafeId=" +
        cafeId +
        "&status=received&printed=0";

      try {
        const result = await axios.get(url, axiosConfig);
        return result.data && result.data.data ? result.data.data : [];
      } catch (err) {
        console.error("Failed to fetch unprinted orders:", err);
        return [];
      }
    },
    async getCartOrderDetails(cartOrderIds) {
      let axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      let url =
        BASE_URI + "/cart/admin/get-cart-orders/?cartOrderIds=" + cartOrderIds;

      try {
        const result = await axios.get(url, axiosConfig);
        return result.data && result.data.data ? result.data.data : [];
      } catch (err) {
        console.error("Failed to fetch cart order details:", err);
        return [];
      }
    },
    async markOrderAsPrinted(orderId) {
      let axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      const updateData = {
        orderId: orderId,
        printed: true,
      };

      try {
        await axios.put(
          BASE_URI + "/orders/update-order",
          updateData,
          axiosConfig
        );
        return true;
      } catch (err) {
        console.error("Failed to mark order as printed:", err);
        return false;
      }
    },
    async autoPrintOrders() {
      // Guard against concurrent execution
      if (this.isAutoPrinting) {
        console.log("[AutoPrint] Already running, skipping");
        return;
      }

      this.isAutoPrinting = true;

      try {
        // Retry marking any pending orders from previous sessions
        await this.retryPendingMarkOrders();

        const cafeId = localStorage.getItem("cafeId");
        const unprintedOrders = await this.getUnprintedOrders(cafeId);

        // Filter out orders we've already printed locally
        const ordersToPrint = unprintedOrders.filter(
          (order) => !this.printedOrderIds.includes(order.uuid)
        );

        console.log("[AutoPrint] Orders to print:", ordersToPrint.length);

        for (const order of ordersToPrint) {
          try {
            // Fetch cart order details
            const cartOrders = await this.getCartOrderDetails(
              order.cartOrderIds
            );

            if (cartOrders.length === 0) {
              console.warn(
                `[AutoPrint] No cart orders found for ${order.uuid}`
              );
              continue;
            }

            // Print the order ticket
            const printResult = await printOrderTicket(order, cartOrders);

            if (printResult.success) {
              // Mark as printed in database
              const marked = await this.markOrderAsPrinted(order.uuid);

              // Add to local tracking
              this.printedOrderIds.push(order.uuid);
              this.savePrintedOrdersToStorage();

              if (marked) {
                // Remove from pending if it was there
                this.pendingMarkOrders = this.pendingMarkOrders.filter(
                  (id) => id !== order.uuid
                );
                this.savePendingMarkOrders();

                await this.showToast(
                  `Order #${order.uuid.substring(0, 6)} printed`,
                  "success"
                );
              } else {
                // Print succeeded but marking failed - add to retry list
                if (!this.pendingMarkOrders.includes(order.uuid)) {
                  this.pendingMarkOrders.push(order.uuid);
                  this.savePendingMarkOrders();
                }
                await this.showToast(
                  `Order #${order.uuid.substring(0, 6)} printed (will sync)`,
                  "warning"
                );
              }
              console.log(`[AutoPrint] Order ${order.uuid} printed`);
            } else {
              await this.showToast(
                `Print failed: ${printResult.message}`,
                "danger"
              );
              console.warn("[AutoPrint] Failed:", printResult.message);
            }
          } catch (error) {
            await this.showToast(
              `Error printing order #${order.uuid.substring(0, 6)}`,
              "danger"
            );
            console.error("[AutoPrint] Error:", error);
          }
        }
      } finally {
        this.isAutoPrinting = false;
      }
    },
    async refreshData(cafeId) {
      let axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      // Use lightweight counts endpoint instead of fetching all orders
      let url = BASE_URI + "/orders/admin/counts?cafeId=" + cafeId;

      try {
        const result = await axios.get(url, axiosConfig);
        const counts = result.data?.data || { received: 0, ready: 0 };

        const previousPreparing = this.preparingOrders;

        this.preparingOrders = counts.received;
        this.readyOrders = counts.ready;

        // Play sound only if there are NEW orders (count increased)
        if (this.preparingOrders > previousPreparing) {
          const audio = new Audio("/assets/sounds/neworder.wav");
          audio.play();
        }

        // Auto-print any unprinted orders
        await this.autoPrintOrders();
      } catch (err) {
        console.error("Failed to fetch order counts:", err);
      }
    },
    triggerRefresh() {
      // Trigger immediate refresh when called from child tabs
      this.refreshData(localStorage.getItem("cafeId"));
    },
  },
  async created() {
    // Load persisted auto-print state
    this.loadPrintedOrdersFromStorage();
    this.loadPendingMarkOrders();

    // Initial load of counts and auto-print
    await this.refreshData(localStorage.getItem("cafeId"));

    // Poll for badge counts every 5 seconds (lightweight endpoint)
    this.intervalnotif = setInterval(async () => {
      await this.refreshData(localStorage.getItem("cafeId"));
    }, 5000);
  },
  beforeDestroy() {
    clearInterval(this.intervalnotif);
  },
});
</script>
